import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    FiArrowLeft, FiSave, FiX, FiUser, FiCalendar,
    FiHome, FiLayers, FiTool, FiFileText, FiTrash2,
    FiPlus, FiCheck, FiAlertCircle
} from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";
import {
    updateAssignment,
    updateJobQuotes,
    updateJobFloors,
    updateJobFloorSpecs,
    deleteJobFiles,
    logJobAction
} from "../../lib/supabase/edit-assignment";

import {
    getSingleAssignment,
    uploadJobFile,
} from "../../lib/supabase/single-assignment";

import DragDropUploader from "../common/DragDropUploader"


const InfoCard = ({ title, children, icon: Icon, color = "blue" }) => (
    <div className="bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2">
            <Icon className={`w-5 h-5 text-${color}-500`} />
            <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
        </div>
        <div className="space-y-2">
            {children}
        </div>
    </div>
);

const CheckboxButton = ({ label, isChecked, onClick, className = "" }) => (
    <button
        type="button"
        onClick={onClick}
        className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 font-medium text-sm flex items-center gap-2 ${isChecked
            ? 'bg-green-500 text-white border-green-500 shadow-md'
            : 'bg-white dark:bg-zinc-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-zinc-600 hover:border-green-300 hover:bg-green-50 dark:hover:bg-green-900/20'
            } ${className}`}
    >
        {isChecked ? <FiCheck className="w-4 h-4" /> : <FiX className="w-4 h-4" />}
        {label}
    </button>
);

const SelectionButton = ({ label, isSelected, onClick, className = "" }) => (
    <button
        type="button"
        onClick={onClick}
        className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 font-medium ${isSelected
            ? 'bg-blue-500 text-white border-blue-500 shadow-md'
            : 'bg-white dark:bg-zinc-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-zinc-600 hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20'
            } ${className}`}
    >
        {label}
    </button>
);

const EditAssignment = () => {
    const { userProfile, company } = useAuth();
    const { companySlug, jobId } = useParams();
    const navigate = useNavigate();

    const [clearPdfs, setClearPdfs] = useState(false);


    const [formData, setFormData] = useState({
        customerName: "",
        customerNumber: "",
        customerMail: "",
        carpenterSupervisorName: "",
        carpenterSupervisorNumber: "",
        customerAddress: "",
        jobType: "frame_and_truss",
        quotes: {
            roof: { isRequired: false },
            wall: { isRequired: false },
            floor: { isRequired: false }
        },
        roofType: "",
        roofTrussCentres: "",
        isSingleStorey: true,
        singleStorey: {
            nonLoadBearingWallStudSizes: "90x45",
            nonLoadBearingWallStudCentres: "",
            wallHeight: ""
        },
        doubleStorey: {
            groundFloor: {
                nonLoadBearingWallStudSizes: "90x45",
                nonLoadBearingWallStudCentres: "",
                wallHeight: ""
            },
            firstFloor: {
                nonLoadBearingWallStudSizes: "90x45",
                nonLoadBearingWallStudCentres: "",
                wallHeight: ""
            }
        },
        floors: [{
            floorNumber: 1,
            floorTrussSize: "",
            flooring: "",
            balconyIncluded: false
        }],
        siteMeasureRequired: false,
        siteMeasureWall: false,
        siteMeasureFloor: false,
        siteMeasureTruss: false,
        isUnit: false,
        unitCount: "",
        urgentQuote: false,
        urgentDesign: false,
        invoiced: false,
        expectedEstimationFinishDate: ""
    });

    const [originalAssignment, setOriginalAssignment] = useState(null);
    const [newFiles, setNewFiles] = useState([]);
    const [filesToDelete, setFilesToDelete] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        loadAssignment();
    }, [jobId, company?.id]);

    const loadAssignment = async () => {
        try {
            setLoading(true);
            const assignment = await getSingleAssignment(jobId, company.id);
            setOriginalAssignment(assignment);

            console.log("Assignment data:", assignment); // Debug log

            // Map assignment data to form structure
            const quotes = {};
            assignment.job_quotes?.forEach(q => {
                quotes[q.quote_type] = {
                    isRequired: q.is_required,
                    expectedDeliveryDate: q.expected_delivery_date ? new Date(q.expected_delivery_date).toISOString().slice(0, 10) : ""
                };
            });

            // Get floor specs
            const singleStoreySpec = assignment.job_floor_specs?.find(s => s.floor_level === 'single_storey');
            const groundFloorSpec = assignment.job_floor_specs?.find(s => s.floor_level === 'ground');
            const firstFloorSpec = assignment.job_floor_specs?.find(s => s.floor_level === 'first');

            console.log("Floor specs:", { singleStoreySpec, groundFloorSpec, firstFloorSpec }); // Debug log

            setFormData({
                customerName: assignment.customer_name || "",
                customerNumber: assignment.customer_number?.replace("+61", "") || "",
                customerMail: assignment.customer_mail || "",
                carpenterSupervisorName: assignment.carpenter_supervisor_name || "",
                carpenterSupervisorNumber: assignment.carpenter_supervisor_number?.replace("+61", "") || "",
                customerAddress: assignment.customer_address || "",
                jobType: assignment.job_type || "frame_and_truss",
                quotes: quotes,
                roofType: assignment.roof_type || "",
                roofTrussCentres: assignment.roof_truss_centres || "",
                isSingleStorey: assignment.is_single_storey !== false,
                singleStorey: {
                    nonLoadBearingWallStudSizes: singleStoreySpec?.non_load_bearing_wall_stud_sizes || "90x45",
                    nonLoadBearingWallStudCentres: singleStoreySpec?.non_load_bearing_wall_stud_centres || "",
                    wallHeight: singleStoreySpec?.wall_height || ""
                },
                doubleStorey: {
                    groundFloor: {
                        nonLoadBearingWallStudSizes: groundFloorSpec?.non_load_bearing_wall_stud_sizes || "90x45",
                        nonLoadBearingWallStudCentres: groundFloorSpec?.non_load_bearing_wall_stud_centres || "",
                        wallHeight: groundFloorSpec?.wall_height || ""
                    },
                    firstFloor: {
                        nonLoadBearingWallStudSizes: firstFloorSpec?.non_load_bearing_wall_stud_sizes || "90x45",
                        nonLoadBearingWallStudCentres: firstFloorSpec?.non_load_bearing_wall_stud_centres || "",
                        wallHeight: firstFloorSpec?.wall_height || ""
                    }
                },
                floors: assignment.job_floors?.map(f => ({
                    floorNumber: f.floor_number,
                    floorTrussSize: f.floor_truss_size || "",
                    flooring: f.flooring || "",
                    balconyIncluded: f.balcony_included || false
                })) || [{
                    floorNumber: 1,
                    floorTrussSize: "",
                    flooring: "",
                    balconyIncluded: false
                }],
                siteMeasureRequired: assignment.site_measure_required || false,
                siteMeasureWall: assignment.site_measure_wall || false,
                siteMeasureFloor: assignment.site_measure_floor || false,
                siteMeasureTruss: assignment.site_measure_truss || false,
                isUnit: assignment.is_unit || false,
                unitCount: assignment.unit_count?.toString() || "",
                urgentQuote: assignment.urgent_quote || false,
                urgentDesign: assignment.urgent_design || false,
                invoiced: assignment.invoiced || false,
                expectedEstimationFinishDate: assignment.expected_estimation_finish_date ?
                    new Date(assignment.expected_estimation_finish_date).toISOString().slice(0, 16) : ""
            });
        } catch (error) {
            console.error("Error loading assignment:", error);
            alert("Error loading assignment details");
            navigate(`/${companySlug}/assignment/list`);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;

        if (name.includes('.')) {
            handleNestedChange(name, type === 'checkbox' ? checked : value);
            return;
        }

        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    }, []);

    const handleNestedChange = useCallback((path, value) => {
        const keys = path.split('.');
        setFormData(prevState => {
            const newState = { ...prevState };
            let current = newState;

            for (let i = 0; i < keys.length - 1; i++) {
                if (!current[keys[i]]) current[keys[i]] = {};
                current = current[keys[i]];
            }

            current[keys[keys.length - 1]] = value;
            return newState;
        });
    }, []);

    // Helper function to toggle selection - if already selected, reset to empty/initial value
    const handleToggleSelection = useCallback((path, value, initialValue = "") => {
        const keys = path.split('.');
        let current = formData;

        for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) return;
            current = current[keys[i]];
        }

        const currentValue = current[keys[keys.length - 1]];
        const newValue = currentValue === value ? initialValue : value;

        handleNestedChange(path, newValue);
    }, [formData, handleNestedChange]);

    const renderWallSpecs = (prefix, data, title) => (
        <div className="bg-red-100/70 dark:bg-red-900/20 p-4 rounded-lg space-y-4 mt-4">
            <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200">{title}</h4>

            <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Non Load Bearing Wall Stud Sizes
                </p>
                <div className="flex gap-2 sm:flex-row flex-col">
                    <SelectionButton
                        label="90x45"
                        isSelected={data.nonLoadBearingWallStudSizes === "90x45"}
                        onClick={() => handleToggleSelection(`${prefix}.nonLoadBearingWallStudSizes`, "90x45")}
                    />
                    <SelectionButton
                        label="90x35"
                        isSelected={data.nonLoadBearingWallStudSizes === "90x35"}
                        onClick={() => handleToggleSelection(`${prefix}.nonLoadBearingWallStudSizes`, "90x35")}
                    />
                </div>
            </div>

            <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Non Load Bearing Wall Stud Centres
                </p>
                <div className="flex gap-2 sm:flex-row flex-col">
                    <SelectionButton
                        label="450"
                        isSelected={data.nonLoadBearingWallStudCentres === "450"}
                        onClick={() => handleToggleSelection(`${prefix}.nonLoadBearingWallStudCentres`, "450")}
                    />
                    <SelectionButton
                        label="600"
                        isSelected={data.nonLoadBearingWallStudCentres === "600"}
                        onClick={() => handleToggleSelection(`${prefix}.nonLoadBearingWallStudCentres`, "600")}
                    />
                </div>
            </div>

            <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Wall Height</p>
                <input
                    className="w-full px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    type="text"
                    value={data.wallHeight}
                    onChange={(e) => handleNestedChange(`${prefix}.wallHeight`, e.target.value)}
                    placeholder="Wall Height (general without stepdown)"
                />
            </div>
        </div>
    );

    const handleFloorChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            floors: prev.floors.map((floor, i) =>
                i === index ? { ...floor, [field]: value } : floor
            )
        }));
    };

    const addFloor = () => {
        setFormData(prev => ({
            ...prev,
            floors: [...prev.floors, {
                floorNumber: prev.floors.length + 1,
                floorTrussSize: "",
                flooring: "",
                balconyIncluded: false
            }]
        }));
    };

    const removeFloor = (index) => {
        if (formData.floors.length === 1) return;

        setFormData(prev => ({
            ...prev,
            floors: prev.floors.filter((_, i) => i !== index)
                .map((floor, i) => ({ ...floor, floorNumber: i + 1 }))
        }));
    };

    const handlePdfsChange = (pdfFiles) => {
        setNewFiles(pdfFiles);
    };

    const markFileForDeletion = (fileId) => {
        setFilesToDelete(prev => [...prev, fileId]);
    };

    const unmarkFileForDeletion = (fileId) => {
        setFilesToDelete(prev => prev.filter(id => id !== fileId));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.customerName.trim()) {
            newErrors.customerName = "Customer name is required";
        }

        const hasSelectedQuotes = formData.quotes.roof?.isRequired ||
            formData.quotes.wall?.isRequired ||
            formData.quotes.floor?.isRequired;

        if (!hasSelectedQuotes) {
            newErrors.quotes = "At least one section (Roof, Wall, or Floor) must be selected";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            alert("Please fix the validation errors before submitting.");
            return;
        }

        setIsSubmitting(true);

        try {
            // Update main job data
            const jobUpdateData = {
                customer_name: formData.customerName,
                customer_number: formData.customerNumber ? `+61${formData.customerNumber}` : null,
                customer_mail: formData.customerMail,
                carpenter_supervisor_name: formData.carpenterSupervisorName,
                carpenter_supervisor_number: formData.carpenterSupervisorNumber ? `+61${formData.carpenterSupervisorNumber}` : null,
                customer_address: formData.customerAddress,
                job_type: formData.jobType,
                roof_type: formData.roofType,
                roof_truss_centres: formData.roofTrussCentres,
                is_single_storey: formData.isSingleStorey,
                site_measure_required: formData.siteMeasureRequired,
                site_measure_wall: formData.siteMeasureWall,
                site_measure_floor: formData.siteMeasureFloor,
                site_measure_truss: formData.siteMeasureTruss,
                is_unit: formData.isUnit,
                unit_count: formData.unitCount ? parseInt(formData.unitCount) : null,
                urgent_quote: formData.urgentQuote,
                urgent_design: formData.urgentDesign,
                invoiced: formData.invoiced,
                expected_estimation_finish_date: formData.expectedEstimationFinishDate || null
            };

            await updateAssignment(originalAssignment.id, jobUpdateData, company.id);

            // Update quotes
            await updateJobQuotes(originalAssignment.id, formData.quotes);

            // Update floors if floor quote is required
            if (formData.quotes.floor?.isRequired) {
                await updateJobFloors(originalAssignment.id, formData.floors);
            }

            // Update floor specs if wall quote is required
            if (formData.quotes.wall?.isRequired) {
                await updateJobFloorSpecs(originalAssignment.id, {
                    isSingleStorey: formData.isSingleStorey,
                    singleStorey: formData.singleStorey,
                    doubleStorey: formData.doubleStorey
                });
            }

            // Delete marked files
            if (filesToDelete.length > 0) {
                console.log("deleting files")
                await deleteJobFiles(filesToDelete);
            }

            // Upload new files
            for (const file of newFiles) {
                await uploadJobFile(
                    originalAssignment.id,
                    file,
                    'initial',
                    null,
                    userProfile.id,
                    userProfile.full_name || userProfile.username,
                    userProfile.role
                );
            }

            // Log the action
            await logJobAction(
                originalAssignment.id,
                userProfile.id,
                userProfile.full_name || userProfile.username,
                userProfile.role,
                'Job Updated',
                { message: 'Job details were updated' }
            );

            alert("Assignment updated successfully!");
            navigate(`/${companySlug}/assignment/${jobId}`);

        } catch (error) {
            console.error("Error updating assignment:", error);
            alert(error.message || "Error updating assignment");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading || !originalAssignment) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading assignment details...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Header */}
                <div className="flex items-start justify-between border-b border-gray-200 dark:border-zinc-700 pb-6">
                    <div className="flex items-start gap-4">
                        <button
                            type="button"
                            onClick={() => navigate(`/${companySlug}/assignment/${jobId}`)}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-zinc-700 rounded-lg transition-colors"
                        >
                            <FiArrowLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <h1 className="sm:text-3xl text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                Edit {originalAssignment.unique_id}
                            </h1>
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-xs font-medium border border-blue-200 dark:border-blue-800">
                                    EDITING
                                </span>
                                <span className="px-3 py-1 bg-gray-100 dark:bg-zinc-700 text-gray-800 dark:text-gray-300 rounded-full text-xs font-medium">
                                    {originalAssignment.job_type}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Customer Information */}
                <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
                    <InfoCard title="Customer Information" icon={FiUser} color="blue">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Customer Name *
                            </label>
                            <input
                                type="text"
                                name="customerName"
                                value={formData.customerName}
                                onChange={handleInputChange}
                                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.customerName ? 'border-red-500' : 'border-gray-300 dark:border-zinc-600'
                                    }`}
                                placeholder="Enter customer name"
                            />
                            {errors.customerName && (
                                <p className="text-red-500 text-xs mt-1">{errors.customerName}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Customer Phone Number
                            </label>
                            <div className="flex">
                                <span className="inline-flex items-center px-3 py-2 border border-r-0 border-gray-300 dark:border-zinc-600 bg-gray-50 dark:bg-zinc-900 text-gray-500 dark:text-gray-400 text-sm rounded-l-lg">
                                    +61
                                </span>
                                <input
                                    type="tel"
                                    name="customerNumber"
                                    value={formData.customerNumber}
                                    onChange={handleInputChange}
                                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded-r-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter phone number"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Customer Email
                            </label>
                            <input
                                type="email"
                                name="customerMail"
                                value={formData.customerMail}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Enter email address"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Customer Address
                            </label>
                            <textarea
                                name="customerAddress"
                                value={formData.customerAddress}
                                onChange={handleInputChange}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Enter customer address"
                            />
                        </div>
                    </InfoCard>

                    {/* Supervisor Information */}
                    <InfoCard title="Supervisor Information" icon={FiTool} color="green">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Supervisor Name
                            </label>
                            <input
                                type="text"
                                name="carpenterSupervisorName"
                                value={formData.carpenterSupervisorName}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholder="Enter supervisor name"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Supervisor Phone Number
                            </label>
                            <div className="flex">
                                <span className="inline-flex items-center px-3 py-2 border border-r-0 border-gray-300 dark:border-zinc-600 bg-gray-50 dark:bg-zinc-900 text-gray-500 dark:text-gray-400 text-sm rounded-l-lg">
                                    +61
                                </span>
                                <input
                                    type="tel"
                                    name="carpenterSupervisorNumber"
                                    value={formData.carpenterSupervisorNumber}
                                    onChange={handleInputChange}
                                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded-r-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    placeholder="Enter phone number"
                                />
                            </div>
                        </div>
                    </InfoCard>
                </div>

                {/* Job Requirements */}
                <InfoCard title="Job Requirements" icon={FiLayers} color="purple">
                    <div className="!mb-6">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Job Type *
                        </label>
                        <div className="sm:flex-row flex gap-3 flex-col">
                            <SelectionButton
                                label="Design Only"
                                isSelected={formData.jobType === "Design"}
                                onClick={() => setFormData(prev => ({ ...prev, jobType: "Design" }))}
                            />
                            <SelectionButton
                                label="Estimation Only"
                                isSelected={formData.jobType === "Estimation"}
                                onClick={() => setFormData(prev => ({ ...prev, jobType: "Estimation" }))}
                            />
                            <SelectionButton
                                label="Both Design & Estimation"
                                isSelected={formData.jobType === "Design & Estimation"}
                                onClick={() => setFormData(prev => ({ ...prev, jobType: "Design & Estimation" }))}
                            />
                        </div>
                    </div>

                    <div className="!mb-6">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Required Sections *
                        </label>
                        <div className="flex gap-3 flex-wrap flex-col sm:flex-row">
                            <CheckboxButton
                                label="Roof"
                                isChecked={formData.quotes.roof?.isRequired}
                                onClick={() => handleNestedChange('quotes.roof.isRequired', !formData.quotes.roof?.isRequired)}
                            />
                            <CheckboxButton
                                label="Wall"
                                isChecked={formData.quotes.wall?.isRequired}
                                onClick={() => handleNestedChange('quotes.wall.isRequired', !formData.quotes.wall?.isRequired)}
                            />
                            <CheckboxButton
                                label="Floor"
                                isChecked={formData.quotes.floor?.isRequired}
                                onClick={() => handleNestedChange('quotes.floor.isRequired', !formData.quotes.floor?.isRequired)}
                            />
                        </div>
                        {errors.quotes && (
                            <p className="text-red-500 text-xs mt-1">{errors.quotes}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Priority Settings
                        </label>
                        <div className="flex gap-3 flex-wrap flex-col sm:flex-row">
                            <CheckboxButton
                                label="Urgent Quote"
                                isChecked={formData.urgentQuote}
                                onClick={() => setFormData(prev => ({ ...prev, urgentQuote: !prev.urgentQuote }))}
                            />
                            <CheckboxButton
                                label="Urgent Design"


                                isChecked={formData.urgentDesign}
                                onClick={() => setFormData(prev => ({ ...prev, urgentDesign: !prev.urgentDesign }))}
                            />
                        </div>
                    </div>
                </InfoCard>

                {/* Timeline */}
                <InfoCard title="Timeline" icon={FiCalendar} color="orange">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Expected Estimation Finish Date
                        </label>
                        <input
                            className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            type="date"
                            name="expectedEstimationFinishDate"
                            value={
                                formData.expectedEstimationFinishDate
                                    ? formData.expectedEstimationFinishDate.split("T")[0]
                                    : ""
                            }
                            onChange={handleInputChange}
                        />
                    </div>
                </InfoCard>

                {/* Job Specifications */}
                {(formData.quotes?.roof?.isRequired || formData.quotes?.wall?.isRequired || formData.quotes?.floor?.isRequired) && (
                    <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 border-b-2 border-blue-500 pb-2">
                            Job Specifications
                        </h2>

                        {/* Roof Specifications */}
                        {formData.quotes?.roof?.isRequired && (
                            <InfoCard title="Roof Specifications" icon={FiHome} color="blue">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Roof Type *
                                    </label>
                                    <div className="flex gap-2 sm:flex-row flex-col">
                                        <SelectionButton
                                            label="Tiled"
                                            isSelected={formData.roofType === "Tiled"}
                                            onClick={() => {
                                                if (formData.roofType === "Tiled") {
                                                    setFormData(prev => ({ ...prev, roofType: "", roofTrussCentres: "" }));
                                                } else {
                                                    setFormData(prev => ({ ...prev, roofType: "Tiled", roofTrussCentres: "600" }));
                                                }
                                            }}
                                        />
                                        <SelectionButton
                                            label="Colorbond"
                                            isSelected={formData.roofType === "Colorbond"}
                                            onClick={() => setFormData(prev => ({
                                                ...prev,
                                                roofType: prev.roofType === "Colorbond" ? "" : "Colorbond"
                                            }))}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Roof Truss Centres *
                                    </label>
                                    <div className="flex gap-2 flex-wrap">
                                        <SelectionButton
                                            label="600"
                                            isSelected={formData.roofTrussCentres === "600"}
                                            onClick={() => setFormData(prev => ({
                                                ...prev,
                                                roofTrussCentres: prev.roofTrussCentres === "600" ? "" : "600"
                                            }))}
                                        />
                                        {formData.roofType !== "tiled" && (
                                            <SelectionButton
                                                label="900"
                                                isSelected={formData.roofTrussCentres === "900"}
                                                onClick={() => setFormData(prev => ({
                                                    ...prev,
                                                    roofTrussCentres: prev.roofTrussCentres === "900" ? "" : "900"
                                                }))}
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* Expected Delivery Date Roof */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Expected Delivery Date
                                    </label>
                                    <input
                                        className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        type="date"
                                        value={formData?.quotes?.roof?.expectedDeliveryDate || ""}
                                        onChange={(e) => handleNestedChange('quotes.roof.expectedDeliveryDate', e.target.value)}
                                    />
                                </div>
                            </InfoCard>
                        )}

                        {/* Wall Specifications */}
                        {formData.quotes?.wall?.isRequired && (
                            <InfoCard title="Wall Specifications" icon={FiLayers} color="red">
                                <div className="flex gap-2 sm:flex-row flex-col mb-8">
                                    <SelectionButton
                                        label="Single Storey"
                                        isSelected={formData.isSingleStorey === true}
                                        onClick={() => setFormData(prev => ({
                                            ...prev,
                                            isSingleStorey: prev.isSingleStorey === true ? null : true
                                        }))}
                                    />
                                    <SelectionButton
                                        label="Double Storey"
                                        isSelected={formData.isSingleStorey === false}
                                        onClick={() => setFormData(prev => ({
                                            ...prev,
                                            isSingleStorey: prev.isSingleStorey === false ? null : false
                                        }))}
                                    />
                                </div>

                                {formData.isSingleStorey ? (
                                    renderWallSpecs('singleStorey', formData.singleStorey, 'Single Storey Specifications')
                                ) : (
                                    <div className="space-y-4 mt-2">
                                        {renderWallSpecs('doubleStorey.groundFloor', formData.doubleStorey.groundFloor, 'Ground Floor Specifications')}
                                        {renderWallSpecs('doubleStorey.firstFloor', formData.doubleStorey.firstFloor, 'First Floor Specifications')}
                                    </div>
                                )}

                                {/* Expected Delivery Date Wall */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Expected Delivery Date
                                    </label>
                                    <input
                                        className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        type="date"
                                        value={formData?.quotes?.wall?.expectedDeliveryDate || ""}
                                        onChange={(e) => handleNestedChange('quotes.wall.expectedDeliveryDate', e.target.value)}
                                    />
                                </div>
                            </InfoCard>
                        )}

                        {/* Floor Specifications */}
                        {formData.quotes?.floor?.isRequired && (
                            <InfoCard title="Floor Specifications" icon={FiLayers} color="green">
                                <div className="flex items-center justify-between mb-4">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Floor Details</span>
                                    <button
                                        type="button"
                                        onClick={addFloor}
                                        className="flex items-center gap-2 px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
                                    >
                                        <FiPlus className="w-4 h-4" />
                                        Add Floor
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    {formData.floors.map((floor, index) => (
                                        <div key={index} className="border-l-4 border-green-200 dark:border-green-800 pl-4 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <p className="text-sm font-medium text-green-900 dark:text-green-300">
                                                    Floor {floor.floorNumber}
                                                </p>
                                                {formData.floors.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeFloor(index)}
                                                        className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-colors"
                                                    >
                                                        <FiTrash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>

                                            <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Floor Truss Size *
                                                    </label>
                                                    <div className="flex flex-col gap-2">

                                                        <select
                                                            value={floor.floorTrussSize}
                                                            onChange={(e) => handleFloorChange(index, 'floorTrussSize', e.target.value)}
                                                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${errors[`floor${index}TrussSize`] ? 'border-red-500' : 'border-gray-300'}`}
                                                        >
                                                            <option value="" disabled>Select truss size</option>
                                                            <option value="310">310mm</option>
                                                            <option value="370">370mm</option>
                                                            <option value="420">420mm</option>
                                                            <option value="Longreach">Longreach</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Flooring Type *
                                                    </label>
                                                    <div className="flex flex-col gap-2">
                                                        <select
                                                            value={floor.flooring}
                                                            onChange={(e) => handleFloorChange(index, 'flooring', e.target.value)}
                                                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${errors[`floor${index}Flooring`] ? 'border-red-500' : 'border-gray-300'}`}
                                                        >
                                                            <option value="" disabled>Select flooring</option>
                                                            <option value="Yellow tongue">Yellow tongue</option>
                                                            <option value="Compression sheet">Compression sheet</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="flex items-center pt-6">
                                                    <CheckboxButton
                                                        label="Balcony Included"
                                                        isChecked={floor.balconyIncluded}
                                                        onClick={() => {
                                                            const newFloors = [...formData.floors];
                                                            newFloors[index].balconyIncluded = !newFloors[index].balconyIncluded;
                                                            setFormData(prev => ({ ...prev, floors: newFloors }));
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Expected Delivery Date Floor */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Expected Delivery Date
                                    </label>
                                    <input
                                        className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        type="date"
                                        value={formData?.quotes?.floor?.expectedDeliveryDate || ""}
                                        onChange={(e) => handleNestedChange('quotes.floor.expectedDeliveryDate', e.target.value)}
                                    />
                                </div>
                            </InfoCard>
                        )}

                        {/* Site Measure */}
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg space-y-4">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Site Measure</h3>

                            <CheckboxButton
                                label="Site Measure Required"
                                isChecked={formData.siteMeasureRequired}
                                onClick={() => setFormData(prev => ({ ...prev, siteMeasureRequired: !prev.siteMeasureRequired }))}
                            />

                            {formData.siteMeasureRequired && (
                                <div className="flex gap-2 sm:flex-row flex-col">
                                    {formData.quotes.wall?.isRequired && (
                                        <CheckboxButton
                                            label="Wall"
                                            isChecked={formData.siteMeasureWall}
                                            onClick={() => setFormData(prev => ({ ...prev, siteMeasureWall: !prev.siteMeasureWall }))}
                                        />
                                    )}
                                    {formData.quotes.floor?.isRequired && (
                                        <CheckboxButton
                                            label="Floor"
                                            isChecked={formData.siteMeasureFloor}
                                            onClick={() => setFormData(prev => ({ ...prev, siteMeasureFloor: !prev.siteMeasureFloor }))}
                                        />
                                    )}
                                    {formData.quotes.roof?.isRequired && (
                                        <CheckboxButton
                                            label="Truss"
                                            isChecked={formData.siteMeasureTruss}
                                            onClick={() => setFormData(prev => ({ ...prev, siteMeasureTruss: !prev.siteMeasureTruss }))}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Additional Information */}
                <div className="space-y-6">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 border-b-2 border-gray-500 pb-2">
                        Additional Information
                    </h2>

                    <div className="space-y-4">
                        <CheckboxButton
                            label="Is this a unit?"
                            isChecked={formData.isUnit}
                            onClick={() => setFormData(prev => ({ ...prev, isUnit: !prev.isUnit }))}
                        />

                        {formData.isUnit && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Number of Units
                                </label>
                                <input
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    type="number"
                                    value={formData.unitCount}
                                    onChange={(e) => setFormData(prev => ({ ...prev, unitCount: e.target.value }))}
                                    placeholder="How many units?"
                                />
                            </div>
                        )}

                        <CheckboxButton
                            label="Invoiced"
                            isChecked={formData.invoiced}
                            onClick={() => setFormData(prev => ({ ...prev, invoiced: !prev.invoiced }))}
                        />
                    </div>
                </div>

                {/* File Management */}
                <div className="space-y-6">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 border-b-2 border-purple-500 pb-2">
                        Files & Documents
                    </h2>

                    {/* Existing Files */}
                    {originalAssignment.job_files && originalAssignment.job_files.length > 0 && (
                        <div className="space-y-3">
                            <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300">Existing Files</h3>
                            <div className="space-y-2">
                                {originalAssignment.job_files
                                    .filter(file => !filesToDelete.includes(file.id))
                                    .map((file, index) => (
                                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-zinc-900 rounded-lg border border-gray-200 dark:border-zinc-700">
                                            <div className="flex items-center gap-3">
                                                <FiFileText className="w-5 h-5 text-gray-400" />
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white">{file.original_name}</p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                                        Uploaded by {file.uploaded_by_name} •
                                                        {new Date(file.uploaded_at).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => markFileForDeletion(file.id)}
                                                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                title="Mark for deletion"
                                            >
                                                <FiTrash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}

                    {/* Files Marked for Deletion */}
                    {filesToDelete.length > 0 && (
                        <div className="space-y-3">
                            <h3 className="text-lg font-medium text-red-700 dark:text-red-400">Files to be Deleted</h3>
                            <div className="space-y-2">
                                {originalAssignment.job_files
                                    .filter(file => filesToDelete.includes(file.id))
                                    .map((file, index) => (
                                        <div key={index} className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                                            <div className="flex items-center gap-3">
                                                <FiFileText className="w-5 h-5 text-red-400" />
                                                <div>
                                                    <p className="text-sm font-medium text-red-900 dark:text-red-300 line-through">{file.original_name}</p>
                                                    <p className="text-xs text-red-600 dark:text-red-400">Will be deleted on save</p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => unmarkFileForDeletion(file.id)}
                                                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                                title="Restore file"
                                            >
                                                <FiX className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}

                    <div className="space-y-4">
                        <h3 className="text-lg font-medium text-gray-700">Upload New Files</h3>
                        <DragDropUploader
                            onFilesChange={handlePdfsChange}
                            clearFiles={clearPdfs}
                            acceptedTypes=".pdf,.doc,.docx,.jpg,.jpeg,.png,.dwg,.xls,.xlsx"
                        />


                    </div>

                    {/* New Files Preview */}
                    {/* {newFiles.length > 0 && (
                            <div className="space-y-3">
                                <h3 className="text-lg font-medium text-green-700 dark:text-green-400">New Files to Upload</h3>
                                <div className="space-y-2">
                                    {newFiles.map((file, index) => (
                                        <div key={index} className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                            <div className="flex items-center gap-3">
                                                <FiFileText className="w-5 h-5 text-green-500" />
                                                <div>
                                                    <p className="text-sm font-medium text-green-900 dark:text-green-300">{file.name}</p>
                                                    <p className="text-xs text-green-600 dark:text-green-400">
                                                        {(file.size / 1024 / 1024).toFixed(2)} MB • Will be uploaded on save
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeNewFile(index)}
                                                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                title="Remove file"
                                            >
                                                <FiX className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )} */}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-6 border-t border-gray-200 dark:border-zinc-700">
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 sm:flex-none px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-blue-300 transition-colors font-medium flex items-center justify-center gap-2"
                    >
                        {isSubmitting ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Saving...
                            </>
                        ) : (
                            <>
                                <FiSave className="w-4 h-4" />
                                Save Changes
                            </>
                        )}
                    </button>

                    <button
                        type="button"
                        onClick={() => navigate(`/${companySlug}/assignment/${jobId}`)}
                        disabled={isSubmitting}
                        className="flex-1 sm:flex-none px-6 py-3 bg-gray-100 dark:bg-zinc-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-zinc-600 disabled:bg-gray-50 dark:disabled:bg-zinc-800 transition-colors font-medium"
                    >
                        Cancel
                    </button>
                </div>

                {/* Validation Errors Summary */}
                {Object.keys(errors).length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <FiAlertCircle className="w-5 h-5 text-red-500" />
                            <h3 className="text-sm font-medium text-red-800 dark:text-red-300">Please fix the following errors:</h3>
                        </div>
                        <ul className="list-disc list-inside text-sm text-red-700 dark:text-red-400 space-y-1">
                            {Object.values(errors).map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    </div>
                )}

            </form>
        </>
    );
};

export default EditAssignment;


